import React, { useRef, useState } from 'react';
import { X, RotateCcw } from 'lucide-react';

/** Visionneuse plein écran avec zoom (pincement 2 doigts, molette souris, double-tap) + pan au
 * doigt — pour l'image/vidéo d'un exercice ("Voir +"). Gestes implémentés à la main (Pointer
 * Events, multi-touch) car `touch-action: manipulation` est volontairement verrouillé au niveau
 * racine de l'app (comportement "vraie app", anti-zoom navigateur) — le pincement natif ne
 * fonctionnerait donc pas sans cette implémentation dédiée, scopée à cette seule visionneuse. */
export const MediaFullscreenViewer = ({ src, type, onClose }) => {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const pointers = useRef(new Map());
  const pinchStart = useRef(null); // { dist, scale }
  const panStart = useRef(null); // { x, y, posX, posY }
  const lastTapRef = useRef(0);

  const clampScale = (s) => Math.min(4, Math.max(1, s));

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchStart.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale };
      panStart.current = null;
    } else if (pointers.current.size === 1) {
      const now = Date.now();
      if (now - lastTapRef.current < 320) {
        // double-tap : bascule 1x <-> 2.5x
        if (scale > 1) { setScale(1); setPos({ x: 0, y: 0 }); }
        else setScale(2.5);
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
      panStart.current = { x: e.clientX, y: e.clientY, posX: pos.x, posY: pos.y };
      setDragging(true);
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinchStart.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      setScale(clampScale(pinchStart.current.scale * (dist / pinchStart.current.dist)));
    } else if (pointers.current.size === 1 && panStart.current && scale > 1) {
      setPos({ x: panStart.current.posX + (e.clientX - panStart.current.x), y: panStart.current.posY + (e.clientY - panStart.current.y) });
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    panStart.current = null;
    setDragging(false);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (scale <= 1) setPos({ x: 0, y: 0 });
  };

  const onWheel = (e) => {
    e.preventDefault();
    setScale((s) => clampScale(s - e.deltaY * 0.0015));
  };

  const reset = () => { setScale(1); setPos({ x: 0, y: 0 }); };

  return (
    <div
      className="fixed inset-0 z-[150] bg-black flex items-center justify-center overflow-hidden select-none"
      onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}
      data-testid="media-fullscreen-viewer"
    >
      <div
        className="w-full h-full flex items-center justify-center touch-none"
        style={{ cursor: dragging ? 'grabbing' : scale > 1 ? 'grab' : 'default' }}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
        onWheel={onWheel}
      >
        {type === 'video' ? (
          <video src={src} className="max-w-full max-h-full object-contain" style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`, transition: dragging ? 'none' : 'transform 0.15s ease-out' }}
            autoPlay loop muted playsInline controls data-testid="fullscreen-video" />
        ) : (
          <img src={src} alt="" className="max-w-full max-h-full object-contain" style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`, transition: dragging ? 'none' : 'transform 0.15s ease-out' }}
            data-testid="fullscreen-image" draggable={false} />
        )}
      </div>

      {scale > 1 && (
        <button onClick={reset} className="absolute bottom-6 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md text-white text-xs font-bold hover:bg-white/20 transition-colors" data-testid="fullscreen-reset-zoom-btn">
          <RotateCcw className="w-3.5 h-3.5" /> Réinitialiser
        </button>
      )}
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/20 transition-colors" data-testid="fullscreen-close-btn">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};
